import React, { useEffect, useMemo, useState } from "react";
import { downloadFile, api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, PageBody, StatusPill, statusTone } from "@/components/PageChrome";
import { Button, Input } from "@/components/controls";
import {
  Download, RefreshCw, Search, Calendar, Package, Truck, FileBarChart2, TrendingUp,
  Hash, ShoppingCart, Boxes,
} from "lucide-react";
import { T } from "@/lib/testIds";

const TABS = [
  { key: "sales",     label: "Sales",             icon: ShoppingCart },
  { key: "mi",        label: "Marketplace ID",    icon: Hash },
  { key: "sku",       label: "System SKU",        icon: Boxes },
  { key: "monthly",   label: "Monthly Sale",     icon: TrendingUp },
  { key: "daily",     label: "Daily Ship",       icon: Truck },
  { key: "china",     label: "China Ship",       icon: Package },
  { key: "amazon",    label: "Amazon Shipping",  icon: FileBarChart2 },
];

const fmt = (n) => (n == null || n === "" ? "—" : Number(n).toLocaleString());
const fmt2 = (n) => (n == null || n === "" ? "—" : Number(n).toFixed(2));

// ============================================================================
// Monthly Sale
// ============================================================================
function MonthlyPane() {
  const [monthFilter, setMonthFilter] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/reports/monthly-sale", { params: monthFilter ? { month: monthFilter } : {} });
      setRows(data.sort((a, b) => (b.month || "").localeCompare(a.month || "")));
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const totals = useMemo(() => rows.reduce(
    (a, r) => ({ orders: a.orders + (r.orders || 0), revenue: a.revenue + (r.revenue || 0), buy_back: a.buy_back + (r.buy_back || 0) }),
    { orders: 0, revenue: 0, buy_back: 0 }
  ), [rows]);

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Calendar size={14} className="text-slate-500" />
        <span className="text-[12px] font-semibold text-slate-700">Month:</span>
        <Input testId="monthly-filter" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)} placeholder="YYYY-MM (blank = all)" className="w-40" />
        <Button onClick={load} disabled={busy} testId="monthly-load">
          {busy ? <RefreshCw size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Compute
        </Button>
        <div className="ml-auto text-[11.5px] text-slate-500 font-mono" data-testid="monthly-row-count">
          {rows.length} month{rows.length === 1 ? "" : "s"}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-3">
        <SummaryCard label="Total Orders" value={fmt(totals.orders)} tone="indigo" />
        <SummaryCard label="Total Revenue ₹" value={fmt2(totals.revenue)} tone="emerald" />
        <SummaryCard label="Total Buy-Back ₹" value={fmt2(totals.buy_back)} tone="slate" />
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="erp-table" data-testid="monthly-table">
          <thead className="bg-slate-100">
            <tr>
              <th>Month</th>
              <th className="text-right">Orders</th>
              <th className="text-right">Revenue ₹</th>
              <th className="text-right">Buy-Back ₹</th>
              <th className="text-right">Profit ₹</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => {
              const profit = (m.revenue || 0) - (m.buy_back || 0);
              return (
                <tr key={m.month || "unknown"} data-testid={`monthly-row-${m.month}`} className="border-t border-slate-100">
                  <td className="font-mono font-semibold">{m.month || "unknown"}</td>
                  <td className="font-mono text-right">{fmt(m.orders)}</td>
                  <td className="font-mono text-right text-emerald-800">{fmt2(m.revenue)}</td>
                  <td className="font-mono text-right">{fmt2(m.buy_back)}</td>
                  <td className={`font-mono text-right font-semibold ${profit < 0 ? "text-red-700" : "text-indigo-800"}`}>
                    {fmt2(profit)}
                  </td>
                </tr>
              );
            })}
            {!rows.length && <tr><td colSpan={5} className="text-center py-8 text-slate-500 border-t border-slate-100">
              {busy ? "Loading…" : "No sales data. Import some orders first."}
            </td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// Daily Ship
// ============================================================================
function DailyShipPane() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/reports/daily-ship/preview", { params: date ? { date } : {} });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  useEffect(() => { load(); }, [date]); // eslint-disable-line

  const doExport = () => downloadFile(`/reports/daily-ship${date ? `?date=${date}` : ""}`, `daily_ship_${date || "all"}.csv`);

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Calendar size={14} className="text-slate-500" />
        <span className="text-[12px] font-semibold text-slate-700">Shipping Date:</span>
        <Input testId="daily-filter" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40" />
        <button onClick={() => setDate("")} data-testid="daily-clear" className="text-[11px] text-blue-600 hover:underline">clear</button>
        <Button onClick={load} disabled={busy} testId="daily-refresh">
          <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Refresh
        </Button>
        <div className="ml-auto flex items-center gap-2">
          <div className="text-[11.5px] text-slate-500 font-mono" data-testid="daily-row-count">
            {rows.length} order{rows.length === 1 ? "" : "s"}
          </div>
          <Button variant="primary" onClick={doExport} testId="daily-export">
            <Download size={12} /> Export CSV
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="overflow-auto max-h-[calc(100vh-320px)]">
          <table className="erp-table w-full" data-testid="daily-table">
            <thead className="sticky top-0 bg-slate-100 z-10">
              <tr>
                <th>System ID</th>
                <th>Marketplace</th>
                <th>Account</th>
                <th>SKU</th>
                <th className="text-right">Qty</th>
                <th>Recipient</th>
                <th>City · State · PIN</th>
                <th>Courier</th>
                <th>Tracking</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => (
                <tr key={o.id} data-testid={`daily-row-${o.system_id}`} className="border-t border-slate-100 hover:bg-indigo-50/40">
                  <td className="font-mono font-semibold">{o.system_id}</td>
                  <td>{o.marketplace}</td>
                  <td className="font-mono">{o.account_id}</td>
                  <td className="font-mono">{o.system_sku || "—"}</td>
                  <td className="font-mono text-right">{o.qty_purchased}</td>
                  <td className="max-w-[160px] truncate" title={o.recipient_name}>{o.recipient_name || "—"}</td>
                  <td className="text-[11.5px] text-slate-600 max-w-[220px] truncate">
                    {[o.ship_city, o.ship_state, o.ship_postal_code].filter(Boolean).join(" · ") || "—"}
                  </td>
                  <td>{o.courier_name || "—"}</td>
                  <td className="font-mono text-[11px] text-slate-600">{o.tracking_id || "—"}</td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={9} className="text-center py-8 text-slate-500 border-t border-slate-100">
                {busy ? "Loading…" : `No orders in Daily-Ship status${date ? ` for ${date}` : ""}.`}
              </td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// China Ship
// ============================================================================
function ChinaShipPane() {
  const [shipments, setShipments] = useState([]);
  const [selected, setSelected] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");

  const loadShipments = async () => {
    try {
      const { data } = await api.get("/reports/china-ship/shipments");
      setShipments(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const loadRows = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/reports/china-ship/preview", { params: selected ? { shipment_id: selected } : {} });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  useEffect(() => { loadShipments(); }, []);
  useEffect(() => { loadRows(); }, [selected]); // eslint-disable-line

  const doExport = () => {
    if (!selected) { toast.error("Select a shipment first"); return; }
    downloadFile(`/reports/china-ship?shipment_id=${encodeURIComponent(selected)}`, `china_ship_${selected}.csv`);
  };

  const filteredShipments = shipments.filter((s) => !q || (s.shipment_id || "").toLowerCase().includes(q.toLowerCase()));
  const totalQty = rows.reduce((a, r) => a + (Number(r.china_qty_to_order) || 0), 0);
  const totalTransfer = rows.reduce((a, r) => a + (Number(r.china_transfer_price) || 0) * (Number(r.qty_purchased) || Number(r.china_qty_to_order) || 0), 0);

  return (
    <div className="grid grid-cols-[300px_1fr] gap-3">
      {/* Shipment list */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden flex flex-col max-h-[calc(100vh-280px)]">
        <div className="p-2 border-b border-slate-200 flex items-center gap-1.5">
          <Search size={13} className="text-slate-400" />
          <input
            data-testid="china-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search shipment ID…"
            className="flex-1 h-7 px-1.5 text-[12px] border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div className="flex-1 overflow-auto">
          <button
            onClick={() => setSelected("")}
            data-testid="china-shipment-all"
            className={`w-full text-left px-3 py-2 border-l-2 text-[12px] ${!selected ? "border-slate-900 bg-slate-50 font-semibold" : "border-transparent hover:bg-slate-50"}`}
          >
            All shipments <span className="font-mono text-slate-500 ml-1">({shipments.length})</span>
          </button>
          {filteredShipments.map((s) => (
            <button
              key={s.shipment_id}
              onClick={() => setSelected(s.shipment_id)}
              data-testid={`china-shipment-${s.shipment_id}`}
              className={`w-full text-left px-3 py-2 border-l-2 flex items-center justify-between ${selected === s.shipment_id ? "border-slate-900 bg-slate-50" : "border-transparent hover:bg-slate-50"}`}
            >
              <span className="font-mono text-[12px] font-semibold text-indigo-800 truncate">{s.shipment_id}</span>
              <span className="font-mono text-[10.5px] text-slate-500 shrink-0 ml-2">
                {s.orders}o · {s.total_qty}q
              </span>
            </button>
          ))}
          {!shipments.length && (
            <div className="p-4 text-center text-slate-500 text-[12px]">No shipments yet.</div>
          )}
        </div>
      </div>

      {/* Rows */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          {selected ? (
            <span className="font-mono text-[13px] font-semibold text-indigo-800">{selected}</span>
          ) : (
            <span className="text-[13px] font-semibold text-slate-700">All shipments</span>
          )}
          <div className="ml-auto flex items-center gap-3">
            <div className="text-[11.5px] text-slate-600 font-mono" data-testid="china-row-count">
              {rows.length} orders · {totalQty} qty · ₹{totalTransfer.toFixed(2)} transfer
            </div>
            <Button variant="primary" onClick={doExport} disabled={!selected} testId="china-export">
              <Download size={12} /> Export CSV
            </Button>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="overflow-auto max-h-[calc(100vh-320px)]">
            <table className="erp-table w-full" data-testid="china-table">
              <thead className="sticky top-0 bg-slate-100 z-10">
                <tr>
                  <th>System ID</th>
                  <th>China ID</th>
                  <th>Shipment</th>
                  <th>SKU</th>
                  <th className="text-right">Qty</th>
                  <th className="text-right">Price ¥</th>
                  <th className="text-right">Transfer ₹</th>
                  <th className="text-right">Weight (g)</th>
                  <th>Status</th>
                  <th>Order Date</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => (
                  <tr key={o.id} className="border-t border-slate-100 hover:bg-indigo-50/40">
                    <td className="font-mono font-semibold">{o.system_id}</td>
                    <td className="font-mono">{o.china_id ?? "—"}</td>
                    <td className="font-mono text-indigo-800">{o.china_shipment_id || "—"}</td>
                    <td className="font-mono">{o.system_sku || "—"}</td>
                    <td className="font-mono text-right">{o.china_qty_to_order ?? o.qty_purchased ?? "—"}</td>
                    <td className="font-mono text-right">{fmt2(o.china_price)}</td>
                    <td className="font-mono text-right text-emerald-800">{fmt2(o.china_transfer_price)}</td>
                    <td className="font-mono text-right">{o.china_weight_gram ?? "—"}</td>
                    <td><StatusPill value={o.china_order_status} tone={statusTone(o.china_order_status)} /></td>
                    <td className="font-mono text-[11px]">{o.china_order_date || "—"}</td>
                  </tr>
                ))}
                {!rows.length && <tr><td colSpan={10} className="text-center py-8 text-slate-500 border-t border-slate-100">
                  {busy ? "Loading…" : selected ? `No orders in shipment ${selected}` : "Select a shipment to see its orders."}
                </td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Amazon Shipping Template
// ============================================================================
function AmazonPane() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/reports/amazon-shipping-template/preview");
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  useEffect(() => { load(); }, []);
  const doExport = () => downloadFile("/reports/amazon-shipping-template", "amazon_shipping.csv");

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[12px] font-semibold text-slate-700">Amazon-marketplace orders with shipping info</span>
        <Button onClick={load} disabled={busy}>
          <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Refresh
        </Button>
        <div className="ml-auto flex items-center gap-2">
          <div className="text-[11.5px] text-slate-500 font-mono" data-testid="amazon-row-count">
            {rows.length} order{rows.length === 1 ? "" : "s"}
          </div>
          <Button variant="primary" onClick={doExport} testId="amazon-export">
            <Download size={12} /> Export CSV
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="overflow-auto max-h-[calc(100vh-320px)]">
          <table className="erp-table w-full" data-testid="amazon-table">
            <thead className="sticky top-0 bg-slate-100 z-10">
              <tr>
                <th>Order ID</th>
                <th>Item ID</th>
                <th className="text-right">Qty</th>
                <th>Ship Date</th>
                <th>Carrier</th>
                <th>Tracking</th>
                <th>Method</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => (
                <tr key={o.id} className="border-t border-slate-100 hover:bg-indigo-50/40">
                  <td className="font-mono text-[11px]">{o.order_id || "—"}</td>
                  <td className="font-mono text-[11px]">{o.order_item_id || "—"}</td>
                  <td className="font-mono text-right">{o.qty_purchased}</td>
                  <td className="font-mono">{o.shipping_date || "—"}</td>
                  <td>{o.courier_name || "—"}</td>
                  <td className="font-mono text-[11px]">{o.tracking_id || "—"}</td>
                  <td>{o.ship_service_name || "—"}</td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={7} className="text-center py-8 text-slate-500 border-t border-slate-100">
                {busy ? "Loading…" : "No Amazon orders."}
              </td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, tone }) {
  const toneMap = {
    indigo: "text-indigo-800",
    emerald: "text-emerald-800",
    slate: "text-slate-700",
    amber: "text-amber-800",
    blue: "text-blue-800",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-3">
      <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`font-mono text-xl font-semibold ${toneMap[tone] || "text-slate-800"}`}>{value}</div>
    </div>
  );
}

// ============================================================================
// Phase 5 — Sales Report (date range + optional account filter)
// ============================================================================
function DateRangeBar({ start, setStart, end, setEnd, accountId, setAccountId, showAccount, onLoad, busy }) {
  return (
    <div className="flex items-center gap-2 mb-3 flex-wrap">
      <Calendar size={14} className="text-slate-500" />
      <span className="text-[12px] font-semibold text-slate-700">From:</span>
      <Input testId="rpt-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" />
      <span className="text-[12px] font-semibold text-slate-700">To:</span>
      <Input testId="rpt-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40" />
      {showAccount && (<>
        <span className="text-[12px] font-semibold text-slate-700 ml-2">Account:</span>
        <Input testId="rpt-account" value={accountId} onChange={(e) => setAccountId(e.target.value)} placeholder="all" className="w-40" />
      </>)}
      <Button onClick={onLoad} disabled={busy} testId="rpt-load">
        <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> {busy ? "Loading…" : "Compute"}
      </Button>
    </div>
  );
}

function todayIso(offset = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}

function SalesPane() {
  const [start, setStart] = useState(todayIso(-90));
  const [end, setEnd] = useState(todayIso(0));
  const [accountId, setAccountId] = useState("");
  const [data, setData] = useState({ rows: [], totals: {} });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const params = { start, end };
      if (accountId) params.account_id = accountId;
      const { data } = await api.get("/reports/sales", { params });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const doExport = () => {
    const params = new URLSearchParams({ start, end, format: "csv" });
    if (accountId) params.set("account_id", accountId);
    downloadFile(`/reports/sales?${params.toString()}`, "sales_report.csv");
  };

  return (
    <div>
      <DateRangeBar start={start} setStart={setStart} end={end} setEnd={setEnd}
                    accountId={accountId} setAccountId={setAccountId} showAccount
                    onLoad={load} busy={busy} />
      <div className="grid grid-cols-3 gap-3 mb-3">
        <SummaryCard label="Order Count" value={fmt(data.totals?.order_count)} tone="indigo" />
        <SummaryCard label="Total Qty" value={fmt(data.totals?.total_qty_purchased)} tone="blue" />
        <SummaryCard label="Total Item Price ₹" value={fmt2(data.totals?.total_item_price)} tone="emerald" />
      </div>
      <div className="flex justify-end mb-2">
        <Button variant="primary" onClick={doExport} testId="sales-export"><Download size={12} /> Export CSV</Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="erp-table" data-testid="sales-table">
          <thead className="bg-slate-100">
            <tr>
              <th>Account ID</th>
              <th>Marketplace</th>
              <th className="text-right">Orders</th>
              <th className="text-right">Qty Purchased</th>
              <th className="text-right">Item Price ₹</th>
            </tr>
          </thead>
          <tbody>
            {(data.rows || []).map((r, i) => (
              <tr key={`${r.account_id}-${r.marketplace}-${i}`} data-testid={`sales-row-${r.account_id}-${r.marketplace}`} className="border-t border-slate-100 hover:bg-indigo-50/40">
                <td className="font-mono">{r.account_id}</td>
                <td>{r.marketplace}</td>
                <td className="font-mono text-right">{fmt(r.order_count)}</td>
                <td className="font-mono text-right">{fmt(r.total_qty_purchased)}</td>
                <td className="font-mono text-right">{fmt2(r.total_item_price)}</td>
              </tr>
            ))}
            {!(data.rows || []).length && (
              <tr><td colSpan={5} className="text-center py-8 text-slate-500 border-t border-slate-100">
                {busy ? "Loading…" : "No orders in this date range."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// Phase 5 — Marketplace-Identifier Report
// ============================================================================
function MarketplaceIdPane() {
  const [start, setStart] = useState(todayIso(-90));
  const [end, setEnd] = useState(todayIso(0));
  const [data, setData] = useState({ rows: [], totals: {} });
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/reports/marketplace-identifier", { params: { start, end } });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const filtered = useMemo(() => {
    if (!q) return data.rows || [];
    const ql = q.toLowerCase();
    return (data.rows || []).filter((r) =>
      (r.marketplace_identifier || "").toLowerCase().includes(ql) ||
      (r.system_sku || "").toLowerCase().includes(ql)
    );
  }, [data.rows, q]);

  const doExport = () => {
    const params = new URLSearchParams({ start, end, format: "csv" });
    downloadFile(`/reports/marketplace-identifier?${params.toString()}`, "marketplace_identifier_report.csv");
  };

  return (
    <div>
      <DateRangeBar start={start} setStart={setStart} end={end} setEnd={setEnd} onLoad={load} busy={busy} />
      <div className="grid grid-cols-3 gap-3 mb-3">
        <SummaryCard label="Unique Identifiers" value={fmt(data.totals?.unique_identifiers)} tone="indigo" />
        <SummaryCard label="Total Qty" value={fmt(data.totals?.total_qty)} tone="blue" />
        <SummaryCard label="Order Count" value={fmt(data.totals?.order_count)} tone="emerald" />
      </div>
      <div className="flex items-center gap-2 mb-2">
        <div className="relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="mi-search" value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search MI or SKU…"
                 className="h-8 pl-7 pr-2 w-64 text-[13px] border border-slate-200 rounded-sm" />
        </div>
        <div className="ml-auto text-[11.5px] text-slate-500 font-mono">{filtered.length} rows</div>
        <Button variant="primary" onClick={doExport} testId="mi-export"><Download size={12} /> Export CSV</Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="overflow-auto max-h-[calc(100vh-360px)]">
          <table className="erp-table" data-testid="mi-table">
            <thead className="sticky top-0 bg-slate-100 z-10">
              <tr>
                <th>Marketplace Identifier</th>
                <th>System SKU</th>
                <th>Marketplace</th>
                <th>Account</th>
                <th className="text-right">Orders</th>
                <th className="text-right">Total Qty</th>
                <th className="text-right">Item Price ₹</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.marketplace_identifier} data-testid={`mi-row-${r.marketplace_identifier}`} className="border-t border-slate-100 hover:bg-indigo-50/40">
                  <td className="font-mono text-[11px]">{r.marketplace_identifier}</td>
                  <td className="font-mono text-[11px]">{r.system_sku}</td>
                  <td>{r.marketplace}</td>
                  <td className="font-mono">{r.account_id}</td>
                  <td className="font-mono text-right">{fmt(r.order_count)}</td>
                  <td className="font-mono text-right font-semibold">{fmt(r.total_qty)}</td>
                  <td className="font-mono text-right">{fmt2(r.total_item_price)}</td>
                </tr>
              ))}
              {!filtered.length && (
                <tr><td colSpan={7} className="text-center py-8 text-slate-500 border-t border-slate-100">
                  {busy ? "Loading…" : "No data."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Phase 5 — System-SKU Report (with inventory context)
// ============================================================================
function SystemSkuPane() {
  const [start, setStart] = useState(todayIso(-90));
  const [end, setEnd] = useState(todayIso(0));
  const [data, setData] = useState({ rows: [], totals: {} });
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/reports/system-sku", { params: { start, end } });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const filtered = useMemo(() => {
    if (!q) return data.rows || [];
    const ql = q.toLowerCase();
    return (data.rows || []).filter((r) =>
      (r.system_sku || "").toLowerCase().includes(ql) ||
      (r.product_type || "").toLowerCase().includes(ql) ||
      (r.variant || "").toLowerCase().includes(ql)
    );
  }, [data.rows, q]);

  const doExport = () => {
    const params = new URLSearchParams({ start, end, format: "csv" });
    downloadFile(`/reports/system-sku?${params.toString()}`, "system_sku_report.csv");
  };

  return (
    <div>
      <DateRangeBar start={start} setStart={setStart} end={end} setEnd={setEnd} onLoad={load} busy={busy} />
      <div className="grid grid-cols-4 gap-3 mb-3">
        <SummaryCard label="Unique SKUs" value={fmt(data.totals?.unique_skus)} tone="indigo" />
        <SummaryCard label="Total Qty Sold" value={fmt(data.totals?.total_qty)} tone="blue" />
        <SummaryCard label="Stock On Hand" value={fmt(data.totals?.stock_qty)} tone="emerald" />
        <SummaryCard label="In Transit" value={fmt(data.totals?.in_transit_qty)} tone="amber" />
      </div>
      <div className="flex items-center gap-2 mb-2">
        <div className="relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="sku-search" value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search SKU / type / variant…"
                 className="h-8 pl-7 pr-2 w-72 text-[13px] border border-slate-200 rounded-sm" />
        </div>
        <div className="ml-auto text-[11.5px] text-slate-500 font-mono">{filtered.length} rows</div>
        <Button variant="primary" onClick={doExport} testId="sku-export"><Download size={12} /> Export CSV</Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="overflow-auto max-h-[calc(100vh-360px)]">
          <table className="erp-table" data-testid="sku-table">
            <thead className="sticky top-0 bg-slate-100 z-10">
              <tr>
                <th>System SKU</th>
                <th>Product Type</th>
                <th>Variant</th>
                <th className="text-right">Orders</th>
                <th className="text-right">Qty Sold</th>
                <th className="text-right">Stock</th>
                <th className="text-right">In Transit</th>
                <th className="text-right">Placed</th>
                <th className="text-right">Dispatched</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.system_sku} data-testid={`sku-row-${r.system_sku}`} className="border-t border-slate-100 hover:bg-indigo-50/40">
                  <td className="font-mono text-[11px]">{r.system_sku}</td>
                  <td>{r.product_type}</td>
                  <td>{r.variant}</td>
                  <td className="font-mono text-right">{fmt(r.order_count)}</td>
                  <td className="font-mono text-right font-semibold">{fmt(r.total_qty)}</td>
                  <td className="font-mono text-right text-emerald-800">{fmt(r.stock_qty)}</td>
                  <td className="font-mono text-right text-amber-700">{fmt(r.in_transit_qty)}</td>
                  <td className="font-mono text-right text-slate-600">{fmt(r.placed_qty)}</td>
                  <td className="font-mono text-right text-blue-800">{fmt(r.dispatched_qty)}</td>
                </tr>
              ))}
              {!filtered.length && (
                <tr><td colSpan={9} className="text-center py-8 text-slate-500 border-t border-slate-100">
                  {busy ? "Loading…" : "No data."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function Reports() {
  const [tab, setTab] = useState("sales");

  return (
    <>
      <PageHeader title="Reports" subtitle="Sales, shipping & procurement dashboards" testId={T.moduleTitle("reports")}>
        <Button
          onClick={() => downloadFile("/reports/all-orders", "all_orders.csv")}
          testId="reports-all-orders-btn"
          title="Full dump of every order with all calculations"
        >
          <Download size={12} /> All Orders CSV
        </Button>
      </PageHeader>
      <PageBody>
        <div className="flex items-center gap-1 mb-4 border-b border-slate-200 overflow-x-auto">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`reports-tab-${key}`}
              className={`h-9 px-3 inline-flex items-center gap-1.5 text-[12.5px] font-semibold border-b-2 -mb-px whitespace-nowrap ${
                tab === key
                  ? "border-slate-900 text-slate-900"
                  : "border-transparent text-slate-500 hover:text-slate-900"
              }`}
            >
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>
        {tab === "sales" && <SalesPane />}
        {tab === "mi" && <MarketplaceIdPane />}
        {tab === "sku" && <SystemSkuPane />}
        {tab === "monthly" && <MonthlyPane />}
        {tab === "daily" && <DailyShipPane />}
        {tab === "china" && <ChinaShipPane />}
        {tab === "amazon" && <AmazonPane />}
      </PageBody>
    </>
  );
}
